import React, { useMemo } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { Flex, Box } from 'rebass/styled-components';

import { Profile } from './steps/profile';
import { useOnboarding } from './hooks/useOnboarding';
import { Stepper } from './components/stepper';
import { NetworkKind } from './steps/networkKinds';
import { Goals } from './steps/goals';
import { Integrations } from './steps/integrations';
import { ShareContacts } from './steps/shareContacts';
import { Team } from './steps/team';

const Walkthrough = () => {
  const {
    isLoadingUpdateUser,
    updateUserProfile,
    handleNextStep,
    goals,
    users,
    setUsers,
    setGoals,
    activeNetwork,
    updateUserGoals,
    isLoadingUpdateUserGoals,
    isLoadingUpdateNetworkKind,
    isLoadingAddAdmin,
    networkKind,
    updateNetworkKind,
    updateGoals,
    user,
    updateTeam,
    showNetworkKindStep,
    profileMode,
    params,
    allIntegrationsDone,
    shouldShowShareContactsStep,
    handleGoogleSyncComplete,
  } = useOnboarding();

  const isOnIntegrationsStep = params?.step === 'integrations';

  const steps = useMemo(() => {
    const result = [];

    // Profile step - show if onboarding not completed or if accessing via employer slug
    if (!user?.onboardingCompleted || params?.employerSlug) {
      result.push('profile');
    }

    if (!activeNetwork?.connect?.hasAccess) {
      return result;
    }

    // Network kind step - show if flag is set or onboarding not completed
    // Skip network-kind step if employerSlug is present
    if (!params?.employerSlug && (showNetworkKindStep || !user?.onboardingCompleted)) {
      result.push('network-kind');
    }

    // Goals step - always included
    result.push('goals');

    // Integrations step - skip only if all integrations are already done AND user is not currently on this step
    // (keeps the step visible while user is completing integrations on that page)
    if (!allIntegrationsDone || isOnIntegrationsStep) {
      result.push('integrations');
    }

    // Share contacts step - show if user has integrations connected and isn't sharing contacts yet
    if (shouldShowShareContactsStep) {
      result.push('share-contacts');
    }

    // Team step - always included
    result.push('team');

    return result;
  }, [
    user?.onboardingCompleted,
    params?.employerSlug,
    activeNetwork?.connect?.hasAccess,
    showNetworkKindStep,
    allIntegrationsDone,
    shouldShowShareContactsStep,
    isOnIntegrationsStep,
  ]);

  return (
    <Box>
      {activeNetwork && <Stepper steps={steps} slug={activeNetwork.slug} />}
      <Flex
        sx={{
          gap: '56px',
          flexDirection: 'column',
          px: '16px',
          mx: 'auto',
          height: '100%',
        }}
      >
        <Switch>
          <Route
            exact
            path={[
              '/networks/:networkSlug/welcome/onboarding/profile',
              '/networks/:networkSlug/company/:employerSlug/welcome/onboarding/profile',
            ]}
          >
            <Redirect
              to={
                params.employerSlug
                  ? `/networks/${activeNetwork.slug}/company/${params.employerSlug}/welcome/onboarding/profile/${profileMode}`
                  : `/networks/${activeNetwork.slug}/welcome/onboarding/profile/${profileMode}`
              }
            />
          </Route>
          <Route
            exact
            path={[
              '/networks/:networkSlug/welcome/onboarding/profile/:mode',
              '/networks/:networkSlug/company/:employerSlug/welcome/onboarding/profile/:mode',
            ]}
          >
            <Profile
              isLoadingUpdateUser={isLoadingUpdateUser}
              updateUserProfile={updateUserProfile}
              network={activeNetwork}
              user={user}
            />
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/network-kind">
            {showNetworkKindStep ? (
              <NetworkKind
                handleNextStep={handleNextStep}
                networkKind={networkKind}
                network={activeNetwork}
                isLoadingUpdateNetworkKind={isLoadingUpdateNetworkKind}
                updateNetworkKind={updateNetworkKind}
              />
            ) : (
              <Redirect to={`/networks/${activeNetwork.slug}/welcome/onboarding/goals`} />
            )}
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/goals">
            <Goals
              handleNextStep={handleNextStep}
              goals={goals}
              updateUserGoals={updateUserGoals}
              isLoadingUpdateUserGoals={isLoadingUpdateUserGoals}
              setGoals={setGoals}
              network={activeNetwork}
              updateGoals={updateGoals}
              kind={networkKind}
            />
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/integrations">
            <Integrations
              handleNextStep={handleNextStep}
              activeNetwork={activeNetwork}
              handleGoogleSyncComplete={handleGoogleSyncComplete}
            />
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/share-contacts">
            {shouldShowShareContactsStep ? (
              <ShareContacts handleNextStep={handleNextStep} activeNetwork={activeNetwork} />
            ) : (
              <Redirect to={`/networks/${activeNetwork?.slug || params?.networkSlug}/welcome/onboarding/team`} />
            )}
          </Route>
          <Route exact path="/networks/:networkSlug/welcome/onboarding/team">
            <Team users={users} setUsers={setUsers} isLoadingAddAdmin={isLoadingAddAdmin} updateTeam={updateTeam} />
          </Route>
        </Switch>
      </Flex>
    </Box>
  );
};

export default Walkthrough;
